import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, describeError } from "@/lib/api";
import { Section, ListCard, Avatar, titleCase } from "./psPrimitives";
import { usePsClient, useMaster, masterKey, BTN2, LINK } from "./psMasters";

// Rendering providers seen in this client's claims that aren't this client's Note-to-Claim providers yet.
export function ClaimsRenderingSection({ providers, all = providers }) {
  const qc = useQueryClient();
  const { cid } = usePsClient();
  const { data: rps = [] } = useMaster("rendering_providers", cid);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const have = new Set(providers.map((p) => String(p.npi_number)));
  const rows = rps.filter((r) => r.npi && !have.has(String(r.npi)));
  if (!rows.length) return null;
  // Add to revQ = create the Note-to-Claim provider (skipped if it already exists) and link it to the client in context.
  const promote = async (r) => {
    const npi = String(r.npi);
    if (!all.some((p) => String(p.npi_number) === npi)) {
      await api.post("/api/edi/note-to-claim/providers", { npi_number: npi, provider_name: r.name, taxonomies: r.taxonomy_code ? [{ code: r.taxonomy_code, desc: r.taxonomy_desc || r.taxonomy_description || "", primary: true }] : [] });
    }
    await api.patch(`/api/edi/note-to-claim/providers/${npi}/client`, { client_id: String(cid) });
  };
  const run = async (list, key) => {
    setBusy(key); setErr("");
    try { for (const r of list) await promote(r); } catch (e) { setErr(describeError(e)); }
    qc.invalidateQueries({ queryKey: ["providers"] }); qc.invalidateQueries({ queryKey: masterKey("rendering_providers", cid) });
    setBusy(null);
  };
  return (
    <Section title={`In your claims, not in revQ yet · ${rows.length}`} testid="ps-claims-rendering"
      action={<button type="button" className={BTN2} disabled={!!busy} onClick={() => run(rows, "all")} data-testid="ps-claims-rendering-addall">{busy === "all" ? "Adding…" : `Add all ${rows.length}`}</button>}>
      <ListCard>
        {rows.map((r, i) => (
          <div key={r.npi} className={`flex items-center gap-3 px-4 py-3 ${i ? "border-t border-[#E9E9EC]" : ""}`} data-testid={`ps-claims-rendering-row-${r.npi}`}>
            <Avatar text={titleCase(r.name || "")} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[var(--ink)] truncate">{titleCase(r.name || "")}</div>
              <div className="text-[11.5px] text-[var(--gray2)] mt-0.5 truncate">{["NPI " + r.npi, r.taxonomy_code, r.taxonomy_desc || r.taxonomy_description].filter(Boolean).join(" · ")}</div>
            </div>
            <button type="button" className={LINK} disabled={!!busy} onClick={() => run([r], r.npi)} data-testid={`ps-claims-rendering-add-${r.npi}`}>{busy === r.npi ? "Adding…" : "Add to revQ"}</button>
          </div>
        ))}
      </ListCard>
      {err ? <div className="mt-2 text-[11.5px] font-semibold text-[#B3261E]">{err}</div> : null}
    </Section>
  );
}
